<template>
  <div class="tab-content company-entitlements">
    <div class="main-header">
      <div>
        <h1>{{ $t('controlCenter.companyEntitlements.title') }}</h1>
        <p class="subtitle">{{ $t('controlCenter.companyEntitlements.subtitle') }}</p>
      </div>
      <button class="btn btn-primary" @click="fetchCompanies" :disabled="loading">
        <i class="fas fa-rotate-right"></i>
        {{ $t('controlCenter.common.refresh') }}
      </button>
    </div>

    <div class="company-summary-grid">
      <div class="summary-panel">
        <span class="summary-label">{{ $t('controlCenter.companyEntitlements.stats.companies') }}</span>
        <strong>{{ companies.length }}</strong>
      </div>
      <div class="summary-panel">
        <span class="summary-label">{{ $t('controlCenter.companyEntitlements.stats.activeCompanies') }}</span>
        <strong>{{ activeCompanyCount }}</strong>
      </div>
      <div class="summary-panel">
        <span class="summary-label">{{ $t('controlCenter.companyEntitlements.stats.entitlements') }}</span>
        <strong>{{ totalEntitlements }}</strong>
      </div>
      <div class="summary-panel">
        <span class="summary-label">{{ $t('controlCenter.companyEntitlements.stats.seats') }}</span>
        <strong>{{ totalSeats }}</strong>
      </div>
    </div>

    <div class="card">
      <div class="card-header filter-row">
        <el-input
          v-model.trim="filters.keyword"
          :placeholder="$t('controlCenter.companyEntitlements.keywordPlaceholder')"
          clearable
          class="keyword-input"
          @keyup.enter.native="fetchCompanies"
        ></el-input>
        <el-select v-model="filters.status" clearable class="filter-select">
          <el-option :label="$t('controlCenter.common.all')" value=""></el-option>
          <el-option v-for="item in statusOptions" :key="item" :label="statusLabel(item)" :value="item"></el-option>
        </el-select>
        <button class="btn btn-primary" @click="fetchCompanies">
          <i class="fas fa-magnifying-glass"></i>
          {{ $t('controlCenter.common.search') }}
        </button>
      </div>

      <div class="card-body">
        <el-table
          :data="companies"
          v-loading="loading"
          row-key="companyKey"
          style="width: 100%"
          :empty-text="$t('controlCenter.companyEntitlements.empty')"
        >
          <el-table-column type="expand">
            <template slot-scope="scope">
              <div class="entitlement-detail">
                <div
                  v-for="item in scope.row.entitlements || []"
                  :key="item.id || item.orderNo"
                  class="entitlement-card"
                >
                  <div class="entitlement-card-header">
                    <div>
                      <div class="entitlement-title">{{ planLabel(item.planId) }}</div>
                      <div class="entitlement-order">{{ item.orderNo || '-' }}</div>
                    </div>
                    <el-tag size="small" :type="statusTagType(item.status)">
                      {{ statusLabel(item.status) }}
                    </el-tag>
                  </div>
                  <div class="entitlement-meta-grid">
                    <div class="meta-item">
                      <span>{{ $t('controlCenter.companyEntitlements.fields.billingCycle') }}</span>
                      <strong>{{ billingCycleLabel(item.billingCycle) }}</strong>
                    </div>
                    <div class="meta-item">
                      <span>{{ $t('controlCenter.companyEntitlements.fields.validity') }}</span>
                      <strong>{{ formatDate(item.startsAt) }} - {{ formatDate(item.expiresAt) }}</strong>
                    </div>
                    <div class="meta-item">
                      <span>{{ $t('controlCenter.companyEntitlements.fields.seats') }}</span>
                      <strong>{{ seatLabel(item) }}</strong>
                    </div>
                    <div class="meta-item">
                      <span>{{ $t('controlCenter.companyEntitlements.fields.updatedAt') }}</span>
                      <strong>{{ formatDateTime(item.updatedAt) }}</strong>
                    </div>
                  </div>
                  <div class="tag-section">
                    <span class="tag-label">{{ $t('controlCenter.companyEntitlements.fields.languages') }}</span>
                    <div class="tag-list">
                      <el-tag
                        v-for="language in item.languages || []"
                        :key="language"
                        size="mini"
                        effect="plain"
                      >
                        {{ languageLabel(language) }}
                      </el-tag>
                      <span v-if="!(item.languages || []).length" class="muted-text">-</span>
                    </div>
                  </div>
                  <div class="tag-section">
                    <span class="tag-label">{{ $t('controlCenter.companyEntitlements.fields.standards') }}</span>
                    <div class="tag-list">
                      <el-tag
                        v-for="standard in item.standards || []"
                        :key="standard"
                        size="mini"
                        effect="plain"
                        type="info"
                      >
                        {{ standardLabel(standard) }}
                      </el-tag>
                      <span v-if="!(item.standards || []).length" class="muted-text">-</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="companyName" :label="$t('controlCenter.companyEntitlements.columns.company')" min-width="220">
            <template slot-scope="scope">
              <div class="company-cell">
                <div class="company-avatar">{{ companyInitial(scope.row.companyName) }}</div>
                <div>
                  <div class="company-name">{{ scope.row.companyName || '-' }}</div>
                  <div class="company-user">{{ scope.row.userId || '-' }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.companyEntitlements.columns.activeEntitlements')" min-width="150">
            <template slot-scope="scope">
              <strong>{{ scope.row.activeEntitlementCount || 0 }}</strong>
              <span class="muted-text"> / {{ scope.row.entitlementCount || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.companyEntitlements.columns.purchasedItems')" min-width="240">
            <template slot-scope="scope">
              <div class="compact-items">
                <el-tag
                  v-for="plan in companyPlans(scope.row)"
                  :key="plan"
                  size="mini"
                  effect="plain"
                >
                  {{ planLabel(plan) }}
                </el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.companyEntitlements.columns.seats')" min-width="150">
            <template slot-scope="scope">
              {{ $t('controlCenter.companyEntitlements.seatSummary', {
                auditors: scope.row.totalAuditorSeats || 0,
                admins: scope.row.totalAdminSeats || 0
              }) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.companyEntitlements.columns.startsAt')" min-width="150">
            <template slot-scope="scope">
              {{ formatDate(scope.row.earliestStartsAt) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.companyEntitlements.columns.expiresAt')" min-width="150">
            <template slot-scope="scope">
              <span :class="{ 'expiry-warning': isExpiringSoon(scope.row.latestExpiresAt) }">
                {{ formatDate(scope.row.latestExpiresAt) }}
              </span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import { paymentAdminApi } from '@/api/paymentAdmin'

export default {
  name: 'CompanyEntitlements',
  data() {
    return {
      loading: false,
      companies: [],
      filters: {
        keyword: '',
        status: ''
      },
      statusOptions: ['ACTIVE', 'PAST_DUE', 'CANCELED', 'FAILED'],
      planOptions: {},
      languageOptions: {},
      standardOptions: {}
    }
  },
  computed: {
    activeCompanyCount() {
      return this.companies.filter(company => Number(company.activeEntitlementCount || 0) > 0).length
    },
    totalEntitlements() {
      return this.companies.reduce((total, company) => total + Number(company.entitlementCount || 0), 0)
    },
    totalSeats() {
      return this.companies.reduce((total, company) => (
        total + Number(company.totalAuditorSeats || 0) + Number(company.totalAdminSeats || 0)
      ), 0)
    }
  },
  created() {
    this.fetchPurchaseConfig()
    this.fetchCompanies()
  },
  methods: {
    async fetchPurchaseConfig() {
      try {
        const response = await paymentAdminApi.getPurchaseConfig()
        const data = response && response.data ? response.data : response
        this.planOptions = data.plans || {}
        this.languageOptions = (data.languages || []).reduce((map, item) => {
          map[item.id] = item.name
          return map
        }, {})
        this.standardOptions = (data.standards || []).reduce((map, item) => {
          map[item.id] = item.name
          return map
        }, {})
      } catch (error) {
        console.error('Failed to fetch purchase config:', error)
      }
    },
    async fetchCompanies() {
      this.loading = true
      try {
        const response = await paymentAdminApi.getCompanyEntitlements({
          keyword: this.filters.keyword || undefined,
          status: this.filters.status || undefined
        })
        const data = response && response.data ? response.data : response
        this.companies = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('Failed to fetch company entitlements:', error)
        this.$message.error(this.$t('controlCenter.companyEntitlements.loadError'))
      } finally {
        this.loading = false
      }
    },
    companyPlans(company) {
      const plans = (company.entitlements || []).map(item => item.planId).filter(Boolean)
      return Array.from(new Set(plans))
    },
    companyInitial(name) {
      return (name || '?').trim().charAt(0).toUpperCase()
    },
    planLabel(planId) {
      const plan = this.planOptions[planId]
      if (plan && plan.name) return plan.name
      const key = `controlCenter.auditDashboard.plans.${planId}`
      const text = this.$t(key)
      return text === key ? planId || '-' : text
    },
    languageLabel(languageId) {
      if (this.languageOptions[languageId]) return this.languageOptions[languageId]
      const key = `buy.languages.items.${languageId}`
      const text = this.$t(key)
      return text === key ? languageId || '-' : text
    },
    standardLabel(standardId) {
      if (this.standardOptions[standardId]) return this.standardOptions[standardId]
      const key = `buy.standards.items.${standardId}.fullname`
      const text = this.$t(key)
      return text === key ? standardId || '-' : text
    },
    billingCycleLabel(value) {
      const key = `controlCenter.billingCycleLabels.${value || 'one_year'}`
      const text = this.$t(key)
      return text === key ? value || '-' : text
    },
    statusLabel(status) {
      const key = `controlCenter.statusLabels.${status || 'UNKNOWN'}`
      const text = this.$t(key)
      return text === key ? status || this.$t('controlCenter.common.unknown') : text
    },
    statusTagType(status) {
      const normalized = (status || '').toUpperCase()
      if (normalized === 'ACTIVE' || normalized === 'SUCCESS') return 'success'
      if (normalized === 'PAST_DUE' || normalized === 'PENDING') return 'warning'
      return 'danger'
    },
    seatLabel(item) {
      return this.$t('controlCenter.companyEntitlements.seatSummary', {
        auditors: item.auditorSeats || 0,
        admins: item.adminSeats || 0
      })
    },
    formatDate(value) {
      if (!value) return '-'
      return String(value).slice(0, 10)
    },
    formatDateTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').slice(0, 19)
    },
    isExpiringSoon(value) {
      if (!value) return false
      const expiryTime = new Date(value).getTime()
      if (Number.isNaN(expiryTime)) return false
      const days = (expiryTime - Date.now()) / (1000 * 60 * 60 * 24)
      return days >= 0 && days <= 30
    }
  }
}
</script>

<style scoped>
.company-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  margin-bottom: 24px;
}

.summary-panel {
  padding: 18px 20px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.summary-label {
  display: block;
  color: #64748b;
  font-size: 13px;
  margin-bottom: 8px;
}

.summary-panel strong {
  color: #0f172a;
  font-size: 26px;
  line-height: 1;
}

.filter-row {
  gap: 12px;
  flex-wrap: wrap;
}

.keyword-input {
  width: 320px;
}

.filter-select {
  width: 180px;
}

.company-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.company-avatar {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #e0f2fe;
  color: #0369a1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.company-name {
  font-weight: 700;
  color: #0f172a;
}

.company-user,
.muted-text {
  color: #94a3b8;
  font-size: 12px;
}

.compact-items,
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.entitlement-detail {
  padding: 18px 28px;
  background: #f8fafc;
  display: grid;
  gap: 14px;
}

.entitlement-card {
  padding: 18px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.18);
}

.entitlement-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.entitlement-title {
  font-weight: 700;
  color: #0f172a;
}

.entitlement-order {
  color: #64748b;
  font-size: 12px;
  margin-top: 3px;
}

.entitlement-meta-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.meta-item {
  padding: 12px;
  border-radius: 8px;
  background: #f8fafc;
}

.meta-item span,
.tag-label {
  display: block;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 6px;
}

.meta-item strong {
  color: #0f172a;
  font-size: 13px;
}

.tag-section {
  margin-top: 10px;
}

.expiry-warning {
  color: #b45309;
  font-weight: 700;
}

@media (max-width: 1100px) {
  .company-summary-grid,
  .entitlement-meta-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .company-summary-grid,
  .entitlement-meta-grid {
    grid-template-columns: 1fr;
  }

  .keyword-input,
  .filter-select {
    width: 100%;
  }

  .summary-panel {
    padding: 14px 16px;
  }

  .summary-panel strong {
    font-size: 22px;
  }

  .company-cell {
    align-items: flex-start;
  }

  .company-name,
  .entitlement-title,
  .entitlement-order,
  .meta-item strong {
    overflow-wrap: anywhere;
  }

  .entitlement-detail {
    padding: 12px;
  }

  .entitlement-card {
    padding: 14px;
  }

  .entitlement-card-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
